import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { YouTubeEmbed } from '@next/third-parties/google';

import SoundOn from '@spa/shared/svg/sound-on';
import SoundOff from '@spa/shared/svg/sound-off';
import ControlPlay from '@spa/shared/svg/control-play';
import { Slider } from '@spa/shared/components/Slider';
import ControlPause from '@spa/shared/svg/control-pause';
import Button from '@spa/shared/components/Unisystem/Button';
import { formatSecondsToMinutes } from '@spa/shared/utils/time';
import { buildQueryParams } from '@spa/shared/utils/buildQueryParams';
import { Wrapper, ControlsWrapper, Controls, ControlButtons } from './styles';

const defaultVideoParams = {
  autoplay: 1,
  showinfo: 0,
  controls: 0,
  disablekb: 0,
  rel: 0,
  enablejsapi: 1,
  origin: process.env.APP_URL,
  widgetid: 1,
  modestbranding: 2
};

const YoutubePlayer = ({ videoKey, styles, videoParams = {}, withControls = true }) => {
  const [player, setPlayer] = useState(null);
  const [triggerGetPlayer, setTriggerGetPlayer] = useState(false);

  const getYTPlayer = async () => {
    const liteYoutube = document.querySelector('lite-youtube');
    if (!liteYoutube?.getYTPlayer) {
      return;
    }

    const youtubePlayer = await document.querySelector('lite-youtube').getYTPlayer();

    setPlayer(youtubePlayer);
  };

  const onPausePlay = () => (player.getPlayerState() === 1 ? player.pauseVideo() : player.playVideo());
  const onMuteUnmute = () => (player.isMuted() ? player.unMute() : player.mute());
  const onProgressClick = e => {
    const progressBarStart = !e.target.offsetLeft ? e.target.offsetParent.offsetLeft : e.target.offsetLeft;
    const progressBarWidth = !e.target.offsetLeft ? e.target.offsetParent.clientWidth : e.target.clientWidth;
    const percentageClicked = ((e.clientX - progressBarStart) * maxProgress) / progressBarWidth;

    player?.seekTo(percentageClicked);
  };

  const progress = player?.playerInfo?.progressState?.current || 0;
  const maxProgress = player?.getDuration() || 100;

  useEffect(() => {
    const getYTPlayerTimeout = setTimeout(() => {
      getYTPlayer();
      setTriggerGetPlayer(!triggerGetPlayer);
    }, 100);

    return () => clearTimeout(getYTPlayerTimeout);
  }, [triggerGetPlayer]);

  return (
    <Wrapper>
      <YouTubeEmbed
        videoid={videoKey}
        style={styles}
        params={buildQueryParams({ ...defaultVideoParams, ...videoParams })}
        js-api
      />
      {withControls && (
        <ControlsWrapper>
          <Slider name="progress" min={0} max={maxProgress} value={progress} onClick={onProgressClick} />
          <Controls>
            <ControlButtons>
              <Button
                isGhost
                icon={player?.getPlayerState() === 1 ? ControlPause : ControlPlay}
                onClick={onPausePlay}
              />
              <Button isGhost icon={player?.isMuted() ? SoundOff : SoundOn} onClick={onMuteUnmute} />
            </ControlButtons>
            <div>
              <span>{formatSecondsToMinutes(progress)}</span> / {formatSecondsToMinutes(maxProgress)}
            </div>
          </Controls>
        </ControlsWrapper>
      )}
    </Wrapper>
  );
};

YoutubePlayer.propTypes = {
  videoKey: PropTypes.string.isRequired,
  styles: PropTypes.string,
  videoParams: PropTypes.object,
  withControls: PropTypes.bool
};

export default YoutubePlayer;
